/**
 * Cinematic background layer — subtle particle field + drifting light (Active Theory–style depth).
 * Respects prefers-reduced-motion.
 */

export function initAtmosphereCanvas(): void {
    const gasit = document.getElementById('atmosphere-canvas') as HTMLCanvasElement | null;
    if (!gasit) return;
    const canvas: HTMLCanvasElement = gasit;   // non-null și în funcțiile de mai jos (îngustarea nu trece în ele)

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        canvas.style.display = 'none';
        return;
    }

    const context = canvas.getContext('2d');
    if (!context) return;
    const ctx: CanvasRenderingContext2D = context;

    let w = 0;
    let h = 0;
    let dpr = 1;
    let mx = 0;
    let my = 0;

    type P = { x: number; y: number; vx: number; vy: number; r: number; a: number };
    let pts: P[] = [];
    const N = 72;
    const LINK_DIST = 118;

    function rebuildPoints(): void {
        pts = [];
        for (let i = 0; i < N; i++) {
            pts.push({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.12,
                vy: (Math.random() - 0.5) * 0.12,
                r: Math.random() * 1.1 + 0.25,
                a: Math.random() * 0.28 + 0.12,
            });
        }
    }

    function resize(): void {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        w = window.innerWidth;
        h = window.innerHeight;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        rebuildPoints();
    }

    document.addEventListener(
        'mousemove',
        (e: MouseEvent) => {
            if (w < 1 || h < 1) return;
            mx = ((e.clientX / w) - 0.5) * 16;
            my = ((e.clientY / h) - 0.5) * 16;
        },
        { passive: true }
    );

    let raf = 0;
    let paused = false;
    function tick(tMs: number): void {
        if (paused) return;
        const t = tMs * 0.000015;
        ctx.clearRect(0, 0, w, h);

        ctx.save();
        ctx.translate(mx * 0.35, my * 0.35);

        const gx = w * (0.52 + 0.32 * Math.sin(t * 0.55));
        const gy = h * (0.42 + 0.28 * Math.cos(t * 0.42));
        const grd = ctx.createRadialGradient(gx, gy, 0, gx, gy, Math.max(w, h) * 0.62);
        grd.addColorStop(0, 'rgba(0, 200, 255, 0.055)');
        grd.addColorStop(0.35, 'rgba(80, 40, 160, 0.028)');
        grd.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grd;
        ctx.fillRect(-mx, -my, w + Math.abs(mx) * 2, h + Math.abs(my) * 2);

        const gx2 = w * (0.2 + 0.15 * Math.cos(t * 0.4));
        const gy2 = h * (0.75 + 0.1 * Math.sin(t * 0.35));
        const grd2 = ctx.createRadialGradient(gx2, gy2, 0, gx2, gy2, w * 0.4);
        grd2.addColorStop(0, 'rgba(255, 107, 26, 0.04)');
        grd2.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grd2;
        ctx.fillRect(-mx, -my, w + Math.abs(mx) * 2, h + Math.abs(my) * 2);

        for (const p of pts) {
            p.x += p.vx;
            p.y += p.vy;
            if (p.x < -20) p.x = w + 20;
            if (p.x > w + 20) p.x = -20;
            if (p.y < -20) p.y = h + 20;
            if (p.y > h + 20) p.y = -20;
        }

        for (let i = 0; i < pts.length; i++) {
            for (let j = i + 1; j < pts.length; j++) {
                const dx = pts[i].x - pts[j].x;
                const dy = pts[i].y - pts[j].y;
                const d = Math.hypot(dx, dy);
                if (d < LINK_DIST) {
                    const alpha = (1 - d / LINK_DIST) * 0.07;
                    ctx.strokeStyle = `rgba(120, 210, 255, ${alpha})`;
                    ctx.lineWidth = 0.5;
                    ctx.beginPath();
                    ctx.moveTo(pts[i].x, pts[i].y);
                    ctx.lineTo(pts[j].x, pts[j].y);
                    ctx.stroke();
                }
            }
        }

        for (const p of pts) {
            ctx.fillStyle = `rgba(200, 235, 255, ${p.a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
        raf = requestAnimationFrame(tick);
    }

    resize();
    window.addEventListener('resize', resize, { passive: true });
    raf = requestAnimationFrame(tick);

    document.addEventListener(
        'visibilitychange',
        () => {
            if (document.hidden) {
                paused = true;
                cancelAnimationFrame(raf);
            } else {
                paused = false;
                raf = requestAnimationFrame(tick);
            }
        },
        { passive: true }
    );
}
